import { useOAuthFlow } from "@hooks/use-oauth-flow";
import type { ConnectionStatusResponse } from "@hz/common/api-types/auth";
import { hasPermission } from "@hz/common/roles";

import { useSelector } from "react-redux";

import { Badge, Box, Flex, Spinner, Text } from "@chakra-ui/react";

import ErrorText from "@components/error-text";
import { Button } from "@components/ui/button";
import { toaster } from "@components/ui/toaster";

import { useDisconnectGooleMutation } from "@state/apiSlice/requests";
import { selectInit } from "@state/mondaySlice";

type OAuthConnectorProps = {
  type: "monday" | "google";
  data?: ConnectionStatusResponse;

  isLoading: boolean;
  oAuthCallback?: () => void;
  oAuthDisconnectCallback?: () => void;
};

const OAuthConnector = ({
  type,
  oAuthCallback,
  oAuthDisconnectCallback,
  data,
  isLoading,
}: OAuthConnectorProps) => {
  const { role } = useSelector(selectInit);

  const hasPerm = type === "google" ? hasPermission(role, "configuration", "read") : true;

  const isConnected = type === "google" ? data?.isGoogleConnected : data?.isMondayConnected;

  const [disconnectGoogle, { isLoading: isGoogleDisconnecting }] = useDisconnectGooleMutation();

  const startOAuthFlow = useOAuthFlow(type, oAuthCallback);

  const displayType = type === "monday" ? "monday.com" : "Google";

  const disconnectGoogleHandler = async () => {
    try {
      const res = await disconnectGoogle().unwrap();

      if (res.message) {
        toaster.create({
          description: `${res.message}`,
          type: "success",
        });

        if (oAuthDisconnectCallback) {
          oAuthDisconnectCallback();
        }
      }
    } catch {
      toaster.error({
        title: "Failed to disconnect Google...",
        description: "Please try again...",
      });
    }
  };

  return (
    <Box w="full">
      {isLoading ? (
        <Flex
          w="full"
          justify="center"
          alignItems="center"
          py={8}
        >
          <Spinner size="xl" />
        </Flex>
      ) : (
        <>
          <Flex
            alignItems="center"
            gapX="12px"
            justifyContent="space-between"
          >
            <Text
              fontSize="md"
              fontWeight="medium"
            >
              {displayType}
            </Text>
            {isConnected ? (
              <Badge
                size="sm"
                colorPalette="green"
              >
                Connected
              </Badge>
            ) : (
              <Badge
                size="sm"
                colorPalette="red"
              >
                Disconnected
              </Badge>
            )}
          </Flex>
          <Text
            fontSize="sm"
            color="gray.600"
            mb="24px"
          >
            {type === "monday" ? "Grant our app access to monday.com." : "Grant our app access to Google."}
          </Text>

          {isConnected ? (
            <Button
              size="sm"
              colorPalette="red"
              disabled={type === "monday"}
              loading={isGoogleDisconnecting}
              onClick={disconnectGoogleHandler}
            >
              Disconnect
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={startOAuthFlow}
              colorPalette="blue"
            >
              Connect
            </Button>
          )}

       
        </>
      )}
    </Box>
  );
};

export default OAuthConnector;
